import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/Button'
import { cn } from '@/lib/cn'
import { useTemplates, useDeleteProgram, useProgramById } from '@/hooks/usePrograms'
import { AssignDialog } from './builder/AssignDialog'

const SOURCE_STYLES: Record<string, string> = {
  ai: 'bg-data/15 text-data border-data/30',
  manual: 'bg-muted/10 text-muted border-border-strong',
  vald: 'bg-success/15 text-success border-success/30',
  import: 'bg-gold/15 text-gold border-gold/30',
}

/** Loads a template's full program, then opens the assign dialog (assigning
 *  copies it into a new per-athlete program, leaving the template untouched). */
function AssignTemplate({ id, onClose }: { id: string; onClose: () => void }) {
  const { data } = useProgramById(id, true)
  if (!data) return null
  return <AssignDialog program={data} onClose={onClose} />
}

export default function CoachTemplates() {
  const navigate = useNavigate()
  const { data: templates = [], isLoading } = useTemplates()
  const del = useDeleteProgram()
  const [assignId, setAssignId] = useState<string | null>(null)
  const [confirmDel, setConfirmDel] = useState<string | null>(null)

  return (
    <div className="flex min-h-full flex-col">
      <header className="sticky top-0 z-20 flex items-center gap-3 border-b border-gold/25 bg-surface/95 px-5 py-3 backdrop-blur">
        <button onClick={() => navigate('/coach')} className="text-gold hover:-translate-x-0.5" aria-label="Back">‹</button>
        <h1 className="flex-1 font-display text-xl tracking-wide">Templates</h1>
        <Button size="sm" onClick={() => navigate('/coach/builder')}>+ New Program</Button>
      </header>

      <main className="mx-auto w-full max-w-3xl flex-1 px-5 pb-16 pt-6">
        <p className="mb-5 text-sm text-muted">
          Reusable programs. Assign a template to any athlete — each gets their own editable copy, so
          tweaking one doesn’t change the template or other athletes.
        </p>

        {isLoading ? (
          <div className="space-y-2">
            {[0, 1, 2].map((i) => <div key={i} className="h-16 animate-pulse rounded-card bg-surface-2" />)}
          </div>
        ) : templates.length === 0 ? (
          <div className="rounded-card border border-dashed border-border-strong bg-surface p-8 text-center">
            <p className="font-condensed text-lg font-bold">No templates yet</p>
            <p className="mx-auto mt-2 max-w-sm text-sm text-muted">
              Build or import a program, then hit <span className="text-gold">Save as Template</span> to store
              it here for reuse.
            </p>
            <Button className="mt-4" onClick={() => navigate('/coach/builder')}>Create one</Button>
          </div>
        ) : (
          <div className="space-y-2">
            {templates.map((t) => (
              <div key={t.id} className="rounded-card border border-border bg-gradient-to-b from-surface-2 to-surface p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-condensed text-lg font-bold leading-tight">{t.name}</p>
                    <p className="mt-0.5 text-xs text-muted">
                      {t.weeks} week{t.weeks !== 1 ? 's' : ''} · {t.exercises} exercise{t.exercises !== 1 ? 's' : ''}
                    </p>
                    {t.description && <p className="mt-1 line-clamp-2 text-xs text-subtle">{t.description}</p>}
                  </div>
                  <span
                    className={cn(
                      'flex-shrink-0 rounded-full border px-2.5 py-0.5 font-condensed text-[11px] uppercase tracking-wide',
                      SOURCE_STYLES[t.source ?? 'manual']
                    )}
                  >
                    {t.source}
                  </span>
                </div>

                <div className="mt-3 flex flex-wrap gap-2">
                  <Button size="sm" onClick={() => setAssignId(t.id)}>Assign to athlete</Button>
                  <Button size="sm" variant="secondary" onClick={() => navigate(`/coach/builder?edit=${t.id}&back=/coach/templates`)}>
                    Edit
                  </Button>
                  {confirmDel === t.id ? (
                    <>
                      <Button size="sm" variant="danger" disabled={del.isPending} onClick={() => del.mutate(t.id, { onSuccess: () => setConfirmDel(null) })}>
                        {del.isPending ? 'Deleting…' : 'Yes, delete template'}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => { setConfirmDel(null); del.reset() }}>Cancel</Button>
                    </>
                  ) : (
                    <Button size="sm" variant="danger" className="ml-auto" onClick={() => { del.reset(); setConfirmDel(t.id) }}>
                      🗑 Delete
                    </Button>
                  )}
                </div>
                {confirmDel === t.id && !del.isError && (
                  <p className="mt-2 text-xs text-muted">
                    Deletes the template only — athletes already on a copy of it keep their program and weights.
                  </p>
                )}
                {confirmDel === t.id && del.isError && (
                  <p className="mt-2 text-xs text-danger">{(del.error as Error).message}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </main>

      {assignId && <AssignTemplate id={assignId} onClose={() => setAssignId(null)} />}
    </div>
  )
}
